import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AppUser, UserProfile } from '../types';
import type { ChallengeWithXP } from '../constants/challenges';
import { achievementCatalog, challenges as defaultChallenges } from '../constants/challenges';
import LeaderboardView from './LeaderboardView';
import {
  subscribeToChallengeCompletions,
  subscribeToChallengeProgress,
  type BackendChallengeCompletion,
  type BackendChallengeProgress,
  type BackendAchievement,
} from '../services/challengeEngine';
import RoddoPageShell from './layout/RoddoPageShell';
import RoddoSectionCard from './layout/RoddoSectionCard';
import RoddoPillTabs from './RoddoPillTabs';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import { xpProgress } from '../utils/xp';

interface ChallengesViewProps {
  userProfile: UserProfile;
  users?: Record<string, AppUser>;
  challenges?: ChallengeWithXP[];
  onBack: () => void;
  viewState?: any;
  progressByKey?: Record<string, BackendChallengeProgress>;
  completionsByKey?: Record<string, BackendChallengeCompletion>;
  achievementMap?: Record<string, BackendAchievement>;
  isDarkMode?: boolean;
  onOpenUserProfile?: (userId: string) => void;
}

const METRIC_LABELS: Record<string, string> = {
  uniqueSpeciesCount: 'Unique species',
  uniqueWatersCount: 'Unique waters',
  sharedContributions: 'Shared contributions',
  uniqueTechniquesCount: 'Unique techniques',
};

function iconForChallenge(challenge: any, metricLabel: string): string {
  const text = `${String(challenge?.title ?? '')} ${String(challenge?.description ?? '')} ${String(metricLabel ?? '')}`.toLowerCase();
  if (text.includes('species') || text.includes('fish')) return '🐟';
  if (text.includes('water') || text.includes('lake') || text.includes('river') || text.includes('bay') || text.includes('spot')) return '🗺️';
  if (text.includes('share') || text.includes('community') || text.includes('post') || text.includes('contribution')) return '💬';
  if (text.includes('technique') || text.includes('lure') || text.includes('bait') || text.includes('gear')) return '🎣';
  if (text.includes('streak') || text.includes('daily') || text.includes('week') || text.includes('consisten')) return '🔥';
  if (text.includes('tournament') || text.includes('leaderboard') || text.includes('rank')) return '🏆';
  if (text.includes('catch') || text.includes('log')) return '📘';
  return '✨';
}

function iconToneForChallenge(icon: string) {
  switch (icon) {
    case '🐟':
      return { bg: 'rgba(124,240,196,0.16)', fg: '#DFF6EA', lightBg: 'rgba(124,240,196,0.18)', lightFg: '#145C42' };
    case '🗺️':
      return { bg: 'rgba(124,199,255,0.16)', fg: '#DFF1FF', lightBg: 'rgba(124,199,255,0.18)', lightFg: '#0E5B85' };
    case '💬':
      return { bg: 'rgba(255,159,176,0.16)', fg: '#FFF0F3', lightBg: 'rgba(255,159,176,0.18)', lightFg: '#8B2F47' };
    case '🎣':
      return { bg: 'rgba(246,196,83,0.16)', fg: '#FFE8A3', lightBg: 'rgba(246,196,83,0.20)', lightFg: '#775700' };
    case '🔥':
      return { bg: 'rgba(255,181,107,0.18)', fg: '#FFF0D9', lightBg: 'rgba(255,181,107,0.20)', lightFg: '#8A4B00' };
    case '🏆':
      return { bg: 'rgba(196,181,253,0.16)', fg: '#EFE3FF', lightBg: 'rgba(196,181,253,0.24)', lightFg: '#5B2E91' };
    case '📘':
      return { bg: 'rgba(103,216,255,0.16)', fg: '#E4FBFF', lightBg: 'rgba(103,216,255,0.18)', lightFg: '#145E73' };
    default:
      return { bg: 'rgba(255,255,255,0.08)', fg: '#F3F6FF', lightBg: 'rgba(255,255,255,0.74)', lightFg: '#544C43' };
  }
}

function normalizeKey(value: unknown): string {
  return String(value ?? '').trim().replace(/__all_time$/, '');
}

function normalizeAchievementKey(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/__all_time$/, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function iconForAchievement(entry: any): string {
  const text = `${String(entry?.title ?? '')} ${String(entry?.description ?? '')} ${String(entry?.category ?? '')}`.toLowerCase();
  if (text.includes('catch') || text.includes('fish')) return '🐟';
  if (text.includes('species')) return '🐠';
  if (text.includes('water') || text.includes('explor') || text.includes('map') || text.includes('region')) return '🗺️';
  if (text.includes('community') || text.includes('comment') || text.includes('share') || text.includes('contribution')) return '💬';
  if (text.includes('market') || text.includes('bazaar') || text.includes('trade')) return '🛍️';
  if (text.includes('tournament') || text.includes('champ') || text.includes('win')) return '🏆';
  if (text.includes('streak') || text.includes('consisten') || text.includes('daily')) return '🔥';
  if (text.includes('master')) return '👑';
  return '✨';
}

function findProgressForChallenge(
  progressByKey: Record<string, BackendChallengeProgress>,
  challenge: any,
): BackendChallengeProgress | undefined {
  const keys = [challenge?.key, challenge?.id, challenge?.legacyId].map(normalizeKey).filter(Boolean);
  for (const key of keys) {
    if (progressByKey[key]) return progressByKey[key];
  }
  return undefined;
}

function findCompletionForChallenge(
  completionsByKey: Record<string, BackendChallengeCompletion>,
  challenge: any,
): BackendChallengeCompletion | undefined {
  const keys = [challenge?.key, challenge?.id, challenge?.legacyId].map(normalizeKey).filter(Boolean);
  for (const key of keys) {
    if (completionsByKey[key]) return completionsByKey[key];
  }
  return undefined;
}

const ChallengesView: React.FC<ChallengesViewProps> = ({
  userProfile,
  challenges,
  onBack,
  progressByKey: progressByKeyProp,
  completionsByKey: completionsByKeyProp,
  achievementMap,
  isDarkMode: isDarkModeProp,
  onOpenUserProfile,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'challenges' | 'achievements' | 'leaderboard'>('challenges');
  const [showCompleted, setShowCompleted] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedAchievementId, setExpandedAchievementId] = useState<string | null>(null);
  const [progressByKeyLocal, setProgressByKeyLocal] = useState<Record<string, BackendChallengeProgress>>({});
  const [completionsByKeyLocal, setCompletionsByKeyLocal] = useState<Record<string, BackendChallengeCompletion>>({});

  const isDarkMode = resolveRoddoDarkMode(isDarkModeProp);
  const ui = getRoddoUiTokens(isDarkMode);

  const softCardStyle: React.CSSProperties = {
    background: isDarkMode ? '#101827' : ui.cardBg,
    border: `1px solid ${ui.border}`,
    boxShadow: isDarkMode ? '0 6px 16px rgba(0,0,0,0.14)' : '0 8px 20px rgba(89,65,37,0.07), inset 0 1px 0 rgba(255,255,255,0.56)',
  };
  const mutedCardStyle: React.CSSProperties = {
    background: isDarkMode ? '#0F1828' : ui.cardBgMuted,
    border: `1px solid ${isDarkMode ? 'rgba(132,151,190,0.12)' : 'transparent'}`,
    boxShadow: isDarkMode ? 'none' : '0 5px 14px rgba(89,65,37,0.05), inset 0 1px 0 rgba(255,255,255,0.48)',
  };

  const textPrimary = { color: ui.textPrimary };
  const textSecondary = { color: isDarkMode ? ui.textSecondary : '#5F564C' };
  const textMuted = { color: isDarkMode ? ui.textMuted : '#746B61' };

  const challengeList: ChallengeWithXP[] = useMemo(
    () => (Array.isArray(challenges) && challenges.length > 0 ? challenges : defaultChallenges),
    [challenges],
  );



  useEffect(() => {
    if (!userProfile?.uid) return;
    if (progressByKeyProp || completionsByKeyProp) return undefined;

    const unsubA = subscribeToChallengeProgress(userProfile.uid, setProgressByKeyLocal);
    const unsubB = subscribeToChallengeCompletions(userProfile.uid, setCompletionsByKeyLocal);

    return () => {
      try { unsubA?.(); } catch {}
      try { unsubB?.(); } catch {}
    };
  }, [userProfile?.uid, progressByKeyProp, completionsByKeyProp]);

  const progressByKey = progressByKeyProp ?? progressByKeyLocal;
  const completionsByKey = completionsByKeyProp ?? completionsByKeyLocal;

  const challengeRows = useMemo(() => {
    return challengeList.map((challenge: any) => {
      const progressDoc = findProgressForChallenge(progressByKey, challenge);
      const completionDoc = findCompletionForChallenge(completionsByKey, challenge);

      const current = Math.max(0, Number(progressDoc?.progress ?? (completionDoc ? challenge.goal : 0)));
      const total = Math.max(1, Number(progressDoc?.target ?? challenge.goal ?? 1));
      const completed = !!completionDoc || !!progressDoc?.completed || current >= total;
      const percent = Math.max(0, Math.min(100, (current / total) * 100));
      const completedAtMs = Number(completionDoc?.completedAtMs ?? progressDoc?.completedAtMs ?? 0);
      const metricLabel = METRIC_LABELS[String(progressDoc?.metric || challenge.metric || '')] || String(progressDoc?.metric || challenge.metric || 'Tracked backend metric');
      const rewardXP = Math.max(0, Number(progressDoc?.rewardXp ?? completionDoc?.rewardXp ?? challenge.rewardXP ?? 0));

      return { challenge, current, total, completed, percent, completedAtMs, metricLabel, rewardXP };
    });
  }, [challengeList, progressByKey, completionsByKey]);

  const activeChallenges = useMemo(() => challengeRows.filter((row) => !row.completed), [challengeRows]);
  const completedChallenges = useMemo(() => challengeRows.filter((row) => row.completed), [challengeRows]);
  const visibleRows = showCompleted ? completedChallenges : activeChallenges;

  const completedCount = completedChallenges.length;
  const activeCount = activeChallenges.length;
  const nextUpRow = activeChallenges[0];
  const totalXPCompleted = completedChallenges.reduce((sum, row: any) => sum + Number(row.rewardXP ?? 0), 0);
  const shellBalance = Math.max(0, Number((userProfile as any)?.shells ?? 0));
  const xpInfo = useMemo(() => xpProgress(Number((userProfile as any)?.xp ?? 0)), [userProfile]);

  const achievementRows = useMemo(() => {
    const profileAchievements = Array.isArray((userProfile as any)?.achievements)
      ? ((userProfile as any).achievements as any[])
      : [];
    const unlockedKeys = new Set<string>();

    Object.keys(achievementMap || {}).forEach((key) => unlockedKeys.add(normalizeAchievementKey(key)));
    profileAchievements.forEach((entry) => {
      unlockedKeys.add(normalizeAchievementKey(entry?.key ?? entry?.id ?? entry?.name ?? entry?.title));
    });

    const backendRows = Object.values(achievementMap || {}) as any[];
    const catalogRows = Array.isArray(achievementCatalog) ? (achievementCatalog as any[]) : [];
    const seen = new Set<string>();

    const rows = catalogRows.map((entry: any, index: number) => {
      const id = String(entry?.key ?? entry?.id ?? entry?.title ?? `achievement_${index}`);
      const normalizedKey = normalizeAchievementKey(id);
      seen.add(normalizedKey);
      const backend = backendRows.find((row: any) => normalizeAchievementKey(row?.key ?? row?.id ?? row?.title) === normalizedKey);
      const unlocked = Boolean(backend || unlockedKeys.has(normalizedKey) || entry?.unlocked || entry?.earned || entry?.completed);
      return {
        id,
        key: normalizedKey,
        title: String(entry?.title ?? entry?.name ?? 'Achievement'),
        description: String(entry?.description ?? entry?.summary ?? ''),
        category: String(entry?.category ?? 'mastery'),
        tier: String(entry?.tier ?? entry?.level ?? (unlocked ? 'Unlocked' : 'Locked')),
        rewardXP: Number(backend?.rewardXp ?? entry?.rewardXp ?? entry?.rewardXP ?? entry?.xp ?? 0),
        unlocked,
        unlockedAt: Number(backend?.unlockedAtMs ?? entry?.unlockedAt ?? 0),
      };
    });

    backendRows.forEach((row: any) => {
      const normalizedKey = normalizeAchievementKey(row?.key ?? row?.id ?? row?.title);
      if (!normalizedKey || seen.has(normalizedKey)) return;
      rows.unshift({
        id: String(row?.key ?? row?.id ?? normalizedKey),
        key: normalizedKey,
        title: String(row?.title ?? 'Achievement'),
        description: String(row?.description ?? ''),
        category: String(row?.category ?? 'mastery'),
        tier: String(row?.tier ?? row?.reputationTier ?? 'Unlocked'),
        rewardXP: Number(row?.rewardXp ?? row?.rewardXP ?? 0),
        unlocked: true,
        unlockedAt: Number(row?.unlockedAtMs ?? row?.completedAtMs ?? 0),
      });
    });

    return rows;
  }, [achievementMap, userProfile]);

  const unlockedAchievementRows = useMemo(
    () => achievementRows.filter((row) => row.unlocked),
    [achievementRows],
  );
  const lockedAchievementRows = useMemo(
    () => achievementRows.filter((row) => !row.unlocked),
    [achievementRows],
  );
  const visibleAchievementRows = useMemo(
    () => [...unlockedAchievementRows, ...lockedAchievementRows].slice(0, 18),
    [lockedAchievementRows, unlockedAchievementRows],
  );

  return (
    <RoddoPageShell padding="none" contentClassName="max-w-4xl px-0 pt-3 sm:pt-4 roddo-progress-lane">
      <div key={isDarkMode ? 'dark' : 'light'} className="space-y-3 roddo-progress-board" data-roddo-polish-lane="2">
        <div className="overflow-hidden rounded-[18px] p-3.5 sm:p-4" style={{
                background: ui.cardBgMuted,
                border: 'none',
                boxShadow: isDarkMode
                  ? '0 6px 16px rgba(3,0,16,0.12)'
                  : '0 4px 12px rgba(89,65,37,0.045)',
              }}>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11px] font-black uppercase tracking-[0.22em]" style={textMuted}>Challenge board</div>
              <button
                type="button"
                onClick={onBack}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full transition hover:-translate-y-0.5 active:translate-y-px"
                style={{
                  background: ui.overlaySoft,
                  color: isDarkMode ? '#F8FAFF' : '#2F3D45',
                  border: 'none',
                  boxShadow: isDarkMode ? '0 10px 22px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.10)' : '0 10px 20px rgba(89,65,37,0.10), inset 0 1px 0 rgba(255,255,255,0.82)',
                }}
                aria-label="Back"
              >
                <span className="text-xl font-black leading-none">‹</span>
              </button>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1 className="min-w-0 text-[24px] font-black leading-tight" style={textPrimary}>Challenges</h1>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                <div className="rounded-[18px] px-3 py-2 text-[11px] font-black uppercase tracking-[0.16em]" style={{ background: ui.overlaySoft, color: isDarkMode ? '#F3F6FF' : '#544C43', border: 'none' }}>Completed {completedCount}</div>
                <div
                  className="rounded-[18px] px-3 py-2 text-[11px] font-black uppercase tracking-[0.16em]"
                  style={{
                    background: isDarkMode ? '#8A70F2' : '#2F80ED',
                    color: '#FFFFFF',
                    border: '1px solid rgba(255,255,255,0.22)',
                    boxShadow: isDarkMode ? '0 7px 16px rgba(112,87,232,.18)' : '0 7px 16px rgba(47,128,237,.16)',
                  }}
                >
                  {totalXPCompleted} XP earned
                </div>
              </div>
            </div>

            <div
              className="rounded-[12px] p-2.5.5"
              style={{
                background: 'transparent',
                border: 'none',
                boxShadow: 'none',
              }}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.16em]" style={textMuted}>Trail progress</div>
                  <div className="mt-1 text-[16px] font-black" style={textPrimary}>Level {xpInfo.level} · {xpInfo.rank}</div>
                  <div className="mt-1 text-[11px] font-bold" style={textSecondary}>{activeCount} active runs · {completedCount} cleared</div>
                </div>
                <div className="text-right text-[12px] font-bold" style={textSecondary}>{xpInfo.remaining} XP to next level{nextUpRow ? <div className="mt-1 text-[10px] font-black uppercase tracking-[0.12em]" style={textMuted}>Next: {String((nextUpRow as any).challenge?.title || 'challenge')}</div> : null}</div>
              </div>
              <div className="mt-2.5 h-2.5 overflow-hidden rounded-full" style={{ background: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(91,71,33,0.10)', border: 'none' }}>
                <div className="h-full rounded-full" style={{ width: `${xpInfo.percent}%`, background: isDarkMode ? '#8A70F2' : '#2F80ED' }} />
              </div>
              <div className="mt-2.5 rounded-[14px] px-3 py-2 text-[11px] font-bold leading-5" style={{ background: isDarkMode ? 'rgba(255,224,138,0.10)' : 'rgba(255,224,138,0.24)', color: isDarkMode ? '#FFE8A3' : '#6F4A00', border: 'none' }}>
                🐚 {shellBalance.toLocaleString()} Shell{shellBalance === 1 ? '' : 's'} in wallet. Shells are earned from Roddo progress and avatar rewards — not real-money currency.
              </div>
            </div>


            <div className="pt-1">
              <RoddoPillTabs
                items={[
                  { key: 'challenges', label: 'Runs' },
                  { key: 'achievements', label: 'Badges' },
                  { key: 'leaderboard', label: 'Ranks' },
                ]}
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as 'challenges' | 'achievements' | 'leaderboard')}
                ariaLabel="Progression"
              />
            </div>
          </div>
        </div>

        {activeTab === 'leaderboard' ? (
          <RoddoSectionCard className="p-3.5 sm:p-4" tone="soft" isDarkMode={isDarkMode}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.22em]" style={textMuted}>Rank board</div>
                <h2 className="mt-1 text-[20px] font-black" style={textPrimary}>Leaderboard</h2>
              </div>
            </div>
            <LeaderboardView
              currentUserUid={userProfile?.uid}
              isDarkMode={isDarkMode}
              onOpenUserProfile={onOpenUserProfile}
            />
          </RoddoSectionCard>
        ) : activeTab === 'achievements' ? (
          <RoddoSectionCard className="p-3.5 sm:p-4" tone="soft" isDarkMode={isDarkMode}>
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.22em]" style={textMuted}>Progression vault</div>
                  <h2 className="mt-1 text-[20px] font-black" style={textPrimary}>Achievements</h2>
                  <p className="mt-1 max-w-2xl text-sm font-semibold" style={textSecondary}>
                    Earned badges and long-term Roddo milestones now live with Challenges so the flag icon owns progression.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="rounded-[18px] px-3 py-2 text-[11px] font-black uppercase tracking-[0.16em]" style={{ background: ui.overlaySoft, color: isDarkMode ? '#F3F6FF' : '#544C43', border: 'none' }}>
                    {unlockedAchievementRows.length} unlocked
                  </div>
                  <div className="rounded-[18px] px-3 py-2 text-[11px] font-black uppercase tracking-[0.16em]" style={{ background: isDarkMode ? 'rgba(138,112,242,0.11)' : 'rgba(196,181,253,0.24)', color: isDarkMode ? '#EFE3FF' : '#5B2E91', border: 'none' }}>
                    {Math.max(achievementRows.length - unlockedAchievementRows.length, 0)} remaining
                  </div>
                </div>
              </div>

              {visibleAchievementRows.length === 0 ? (
                <div className="rounded-[12px] p-2.5.5 text-sm" style={{ ...softCardStyle, ...textSecondary }}>
                  No achievements are configured yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {visibleAchievementRows.map((entry) => {
                    const icon = iconForAchievement(entry);
                    const unlocked = Boolean(entry.unlocked);
                    const isExpanded = expandedAchievementId === entry.id;
                    const badgeTone = unlocked
                      ? isDarkMode
                        ? 'rgba(124,240,196,0.16)'
                        : 'rgba(124,240,196,0.20)'
                      : ui.overlaySoft;

                    return (
                      <div
                        key={entry.id}
                        className="relative overflow-hidden rounded-[16px] transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-px"
                        style={{
                          background: ui.cardBg,
                          border: `1px solid ${unlocked ? ui.borderStrong : ui.border}`,
                          boxShadow: unlocked
                            ? (isDarkMode ? '0 14px 30px rgba(124,240,196,0.12)' : '0 12px 24px rgba(89,65,37,0.08)')
                            : (isDarkMode ? '0 10px 22px rgba(0,0,0,0.12)' : '0 8px 18px rgba(89,65,37,0.05)'),
                          opacity: unlocked ? 1 : 0.78,
                        }}
                      >
                        <div className="absolute inset-y-0 left-0 w-[4px]" style={{ background: unlocked ? (isDarkMode ? '#8A70F2' : '#2F80ED') : 'rgba(130,120,110,0.20)' }} />
                        <button
                          type="button"
                          onClick={() => setExpandedAchievementId((prev) => (prev === entry.id ? null : entry.id))}
                          className="block w-full px-3.5 py-3 text-left"
                          aria-expanded={isExpanded}
                        >
                          <div className="grid grid-cols-[48px_minmax(0,1fr)_36px] items-center gap-3 pl-1">
                            <div
                              className="grid h-12 w-12 shrink-0 place-items-center rounded-[13px] text-[21px]"
                              style={{
                                background: badgeTone,
                                border: 'none',
                                boxShadow: unlocked ? (isDarkMode ? '0 10px 20px rgba(124,240,196,0.10)' : '0 8px 16px rgba(89,65,37,0.06)') : 'none',
                              }}
                            >
                              {icon}
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="flex min-w-0 items-start gap-2">
                                <h3 className="min-w-0 whitespace-normal break-words text-[15px] font-black leading-tight" style={textPrimary}>{entry.title}</h3>
                                <span
                                  className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[15px] font-black"
                                  style={{
                                    background: unlocked ? (isDarkMode ? 'rgba(124,240,196,0.16)' : 'rgba(124,240,196,0.18)') : ui.overlaySoft,
                                    color: unlocked ? (isDarkMode ? '#DFF6EA' : '#145C42') : (isDarkMode ? ui.textMuted : '#746B61'),
                                    border: 'none',
                                  }}
                                  aria-label={unlocked ? 'Unlocked' : 'Locked'}
                                  title={unlocked ? 'Unlocked' : 'Locked'}
                                >
                                  {unlocked ? '✨' : '🔒'}
                                </span>
                              </div>

                              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.12em]">
                                <span className="rounded-full px-2 py-0.5" style={{ background: ui.overlaySoft, color: isDarkMode ? '#F3F6FF' : '#544C43', border: 'none' }}>🏷️ {entry.category || 'mastery'}</span>
                                {Number(entry.rewardXP) > 0 ? (
                                  <span className="rounded-full px-2 py-0.5" style={{ background: isDarkMode ? 'rgba(246,196,83,0.16)' : 'rgba(246,196,83,0.22)', color: isDarkMode ? '#FFE8A3' : '#775700', border: 'none' }}>⚡ +{Number(entry.rewardXP)} XP</span>
                                ) : null}
                              </div>
                            </div>

                            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-[11px] text-lg font-black" style={{ background: ui.cardBgMuted, color: ui.textPrimary, border: 'none' }}>
                              {isExpanded ? '−' : '+'}
                            </div>
                          </div>
                        </button>

                        {isExpanded ? (
                          <div className="px-4 pb-4 pl-5">
                            <div className="rounded-[12px] p-2.5.5" style={{
                                background: ui.surfaceBg,
                                border: 'none',
                                boxShadow: isDarkMode
                                  ? '0 4px 12px rgba(3,0,16,0.10)'
                                  : '0 3px 10px rgba(89,65,37,0.04)',
                              }}>
                              <p className="text-[14px] font-semibold leading-6" style={textSecondary}>{entry.description || 'Badge details will appear here as the progression vault grows.'}</p>
                              <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] font-black uppercase tracking-[0.12em]">
                                <div className="rounded-[12px] p-2.5" style={{ background: ui.cardBgMuted, color: ui.textSecondary, border: 'none' }}>🏅 Tier<br /><span className="text-[12px] normal-case tracking-normal" style={textPrimary}>{entry.tier || (unlocked ? 'Unlocked' : 'Locked')}</span></div>
                                <div className="rounded-[12px] p-2.5" style={{ background: ui.cardBgMuted, color: ui.textSecondary, border: 'none' }}>🎒 Status<br /><span className="text-[12px] normal-case tracking-normal" style={textPrimary}>{unlocked ? 'In your vault' : 'Keep fishing'}</span></div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}

              {achievementRows.length > visibleAchievementRows.length ? (
                <div className="rounded-[12px] p-2.5.5 text-sm font-bold" style={{ ...mutedCardStyle, ...textSecondary }}>
                  Showing the first {visibleAchievementRows.length} achievements. The full Achievements route can stay as a legacy/deep-link screen if needed.
                </div>
              ) : null}
            </div>
          </RoddoSectionCard>
        ) : (
          <RoddoSectionCard className="p-3.5 sm:p-4" tone="soft" isDarkMode={isDarkMode}>
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.22em]" style={textMuted}>Live runs</div>
                  <h2 className="mt-1 text-[20px] font-black" style={textPrimary}>{showCompleted ? 'Completed challenges' : 'Active challenges'}</h2>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2 text-[11px] font-black">
                  <span className="rounded-full px-2.5 py-1 uppercase tracking-[0.12em]" style={{ background: ui.overlaySoft, color: isDarkMode ? '#F3F6FF' : '#544C43', border: 'none' }}>🎯 {visibleRows.length} visible</span>
                  <button
                    type="button"
                    onClick={() => setShowCompleted((value) => !value)}
                    className="rounded-full px-2.5 py-1 uppercase tracking-[0.12em] transition hover:-translate-y-0.5 active:translate-y-px"
                    style={{ background: isDarkMode ? 'rgba(138,112,242,0.11)' : 'rgba(196,181,253,0.24)', color: isDarkMode ? '#EFE3FF' : '#5B2E91', border: 'none' }}
                  >
                    {showCompleted ? '🏁 Active' : '✅ Cleared'}
                  </button>
                </div>
              </div>

              {visibleRows.length === 0 ? (
                <div className="rounded-[12px] p-2.5.5 text-sm" style={{ ...softCardStyle, ...textSecondary }}>
                  {showCompleted ? 'No completed challenges yet.' : 'No active challenges right now.'}
                </div>
              ) : (
                <div className="space-y-3">
                  {visibleRows.map((row) => {
                    const { challenge, current, total, completed, percent, metricLabel } = row;
                    const id = String(challenge.key || challenge.id);
                    const isExpanded = expandedId === id;
                    const reward = Number((row as any).rewardXP ?? challenge.rewardXP ?? 0);
                    const challengeIcon = iconForChallenge(challenge, metricLabel);
                    const challengeIconTone = iconToneForChallenge(challengeIcon);

                    return (
                      <div
                        key={id}
                        className="relative overflow-hidden rounded-[16px] transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-px"
                        style={{
                          background: isDarkMode ? '#101827' : ui.cardBg,
                          border: `1px solid ${ui.border}`,
                          boxShadow: isDarkMode ? '0 5px 14px rgba(0,0,0,0.13)' : '0 10px 20px rgba(89,65,37,0.06)',
                        }}
                      >
                        <div className="absolute inset-y-0 left-0 w-[4px]" style={{ background: completed ? '#34D399' : (isDarkMode ? '#8A70F2' : '#2F80ED') }} />
                        <button type="button" onClick={() => setExpandedId((prev) => (prev === id ? null : id))} className="block w-full px-3.5 py-3 text-left">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span
                                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[16px] text-[20px]"
                                  style={{
                                    background: isDarkMode ? challengeIconTone.bg : challengeIconTone.lightBg,
                                    color: isDarkMode ? challengeIconTone.fg : challengeIconTone.lightFg,
                                    border: 'none',
                                    boxShadow: isDarkMode ? '0 8px 16px rgba(0,0,0,0.14)' : '0 6px 12px rgba(89,65,37,0.06)',
                                  }}
                                  aria-hidden="true"
                                >
                                  {challengeIcon}
                                </span>
                                <div className="min-w-0">
                                  <h3 className="text-[16px] font-black" style={{ color: isDarkMode ? '#FAFCFF' : ui.textPrimary }}>{challenge.title}</h3>
                                  <div className="mt-1 text-[11px] font-black uppercase tracking-[0.14em]" style={textMuted}>{metricLabel}</div>
                                </div>
                                <span className="rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em]" style={{ background: completed ? (isDarkMode ? 'rgba(124,240,196,0.14)' : 'rgba(124,240,196,0.18)') : (isDarkMode ? 'rgba(196,181,253,0.14)' : 'rgba(196,181,253,0.20)'), color: completed ? (isDarkMode ? '#DFF6EA' : '#145C42') : (isDarkMode ? '#EFE3FF' : '#5B2E91') }}>
                                  {completed ? 'Trail cleared' : 'On the board'}
                                </span>
                              </div>

                              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm font-semibold">
                                <span style={{ color: isDarkMode ? '#F7E4A0' : '#8A6200' }}>{Math.min(current, total)}/{total}</span>
                                <span style={{ color: isDarkMode ? '#7CF0C4' : '#1D7C63' }}>{Math.round(percent)}%</span>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <div
                                className="rounded-[13px] px-2.5 py-1.5 text-[11px] font-black"
                                style={{
                                  background: isDarkMode ? 'rgba(246,196,83,.16)' : 'rgba(246,196,83,.18)',
                                  color: isDarkMode ? '#FFE8A3' : '#7A5200',
                                  border: isDarkMode ? '1px solid rgba(246,196,83,.22)' : '1px solid rgba(181,126,24,.18)',
                                  boxShadow: 'none',
                                }}
                              >
                                +{reward} XP
                              </div>
                              <div className="grid h-9 w-9 place-items-center rounded-[12px] text-lg font-black" style={{ background: ui.cardBgMuted, color: ui.textPrimary, border: 'none' }}>{isExpanded ? '−' : '+'}</div>
                            </div>
                          </div>

                          <div className="mt-3 h-2.5 overflow-hidden rounded-full" style={{ background: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(91,71,33,0.10)', border: 'none' }}>
                            <div className="h-full rounded-full" style={{ width: `${completed ? 100 : percent}%`, background: completed ? '#34D399' : (isDarkMode ? '#8A70F2' : '#2F80ED') }} />
                          </div>
                        </button>

                        {isExpanded ? (
                          <div className="px-4 pb-3.5">
                            <div className="rounded-[12px] p-2.5.5" style={{
                                background: ui.surfaceBg,
                                border: 'none',
                                boxShadow: isDarkMode
                                  ? '0 4px 12px rgba(3,0,16,0.10)'
                                  : '0 3px 10px rgba(89,65,37,0.04)',
                              }}>
                              <p className="text-[14px] leading-6" style={textSecondary}>🧭 {challenge.description}</p>
                              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                                <div className="rounded-[12px] p-2.5 text-[11px] font-black uppercase tracking-[0.12em]" style={{ background: ui.cardBgMuted, color: ui.textSecondary, border: 'none' }}>🎯 Progress<br /><span className="text-[13px] normal-case tracking-normal" style={textPrimary}>{Math.min(current, total)}/{total}</span></div>
                                <div className="rounded-[12px] p-2.5 text-[11px] font-black uppercase tracking-[0.12em]" style={{ background: ui.cardBgMuted, color: ui.textSecondary, border: 'none' }}>⚡ Reward<br /><span className="text-[13px] normal-case tracking-normal" style={textPrimary}>+{reward} XP</span></div>
                                <div className="rounded-[12px] p-2.5 text-[11px] font-black uppercase tracking-[0.12em]" style={{ background: ui.cardBgMuted, color: ui.textSecondary, border: 'none' }}>🏁 State<br /><span className="text-[13px] normal-case tracking-normal" style={textPrimary}>{completed ? 'Cleared' : 'Tracking'}</span></div>
                              </div>
                              <div className="mt-3 flex flex-wrap gap-2">
                                <span className="rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.14em]" style={{ background: ui.cardBgMuted, color: ui.textSecondary, border: 'none' }}>{metricLabel}</span>
                                <span className="rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.14em]" style={{ background: isDarkMode ? 'rgba(138,112,242,0.10)' : 'rgba(196,181,253,0.22)', color: isDarkMode ? '#EFE3FF' : '#5B2E91' }}>Goal {total}</span>
                                <span className="rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.14em]" style={{ background: isDarkMode ? 'rgba(124,199,255,0.14)' : 'rgba(124,199,255,0.18)', color: isDarkMode ? '#DFF1FF' : '#0E5B85' }}>{completed ? 'Cleared' : 'Live tracking'}</span>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </RoddoSectionCard>
        )}
      </div>
    </RoddoPageShell>
  );
};

export default ChallengesView;
